'use client';

import {MouseEvent, memo, useRef} from 'react';
import {Rnd, RndDragCallback, RndResizeCallback} from 'react-rnd';
import {useTransformComponent} from 'react-zoom-pan-pinch';
import {Popper, PopperHandle} from '../../../components';
import {cn} from '../../../libs';
import {CIRCLE_SIZE} from '../../../spotlight/libs';
import {useTransformScale} from '../../hooks';
import {useRectStore} from '../../stores';
import {Rect} from '../../types';
import {RectMenu} from '../RectMenu';
import {Blur} from './Blur';
import {Circle} from './Circle';
import {HighlightRect} from './HighlightRect';
import {RectLabel} from './RectLabel';

interface RndRectProps {
  index: number;
  rect: Rect;
  edit?: boolean;
}

export const RndRect = memo(({rect, index, edit = true}: RndRectProps) => {
  const popperRef = useRef<PopperHandle>(null);
  const {changeRect, selectRect, removeRect} = useRectStore();

  const scale = useTransformScale();
  const {disabled} = useTransformComponent(({instance}) => {
    return {
      disabled: instance.props.disabled,
    };
  });

  const onDrag: RndDragCallback = () => {
    if (popperRef.current) {
      requestAnimationFrame(() => {
        popperRef.current?.update();
      });
    }
  };

  const onDragStop: RndDragCallback = (_e, data) => {
    if (rect.type === 'circle') {
      changeRect({x: data.x + CIRCLE_SIZE / 2, y: data.y + CIRCLE_SIZE / 2});
    } else {
      changeRect({x: data.x + 4, y: data.y + 4});
    }
  };

  const onResizeStop: RndResizeCallback = (_e, _dir, _elementRef, delta, position) => {
    return changeRect({
      width: rect.width + delta.width,
      height: rect.height + delta.height,
      x: position.x + 4,
      y: position.y + 4,
    });
  };

  const onSelectRect = () => {
    selectRect(index);
  };

  const onHover = (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
    if (disabled || edit || rect.type === 'blur') return;
    const target = e.currentTarget as HTMLElement;

    const scaleX = (target.clientWidth + 8) / target.clientWidth;
    const scaleY = (target.clientHeight + 8) / target.clientHeight;

    target.style.scale = `${scaleX} ${scaleY}`;
  };

  const onMouseLeave = (e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
    if (disabled || edit || rect.type === 'blur') return;
    const target = e.currentTarget as HTMLElement;
    target.style.scale = '100%';
  };

  return (
    <Rnd
      id={`rect-${index}`}
      className={cn(rect.type === 'highlight' ? 'z-[100]' : 'z-[90]', rect.active && 'z-[200]')}
      size={{
        width: rect.type === 'circle' ? CIRCLE_SIZE : rect.width + 8,
        height: rect.type === 'circle' ? CIRCLE_SIZE : rect.height + 8,
      }}
      position={{
        x: rect.type === 'circle' ? rect.x - CIRCLE_SIZE / 2 : rect.x - 4,
        y: rect.type === 'circle' ? rect.y - CIRCLE_SIZE / 2 : rect.y - 4,
      }}
      bounds="parent"
      scale={scale}
      enableResizing={rect.type !== 'circle' && edit}
      disableDragging={!edit}
      onResizeStart={onSelectRect}
      onDrag={onDrag}
      onDragStop={onDragStop}
      onResizeStop={onResizeStop}
      onMouseDown={onSelectRect}
    >
      <Popper
        ref={popperRef}
        menu={<RectMenu rect={rect} onChange={changeRect} onDelete={removeRect} />}
        placement="top"
        width="auto"
        click={{event: 'mousedown', toggle: false, stickIfOpen: true, enabled: edit}}
      >
        <div
          className={cn('absolute size-full transition-all duration-100')}
          onMouseEnter={onHover}
          onMouseLeave={onMouseLeave}
        >
          {rect.type === 'highlight' && <HighlightRect rect={rect} scale={scale} />}
          {rect.type === 'circle' && <Circle active={rect.active} scale={scale} />}
          {rect.type === 'blur' && <Blur active={rect.active} scale={scale} edit={edit} />}
        </div>
      </Popper>

      {rect.type === 'highlight' && <RectLabel rect={rect} scale={scale} />}
    </Rnd>
  );
});
